(function () {
  'use strict';

  // The latke box (sections/latke-box.liquid). A set order size, or the
  // slider and number field behind "Custom size", sets how many latkes the
  // customer wants; each one falls into the box (a photo, the section's
  // background), and the "Pick sides" link carries the count on as
  // ?qty=<handle>@<pack>:<packs>. Latkes come in packs (data-pack, 6): the
  // count is always a whole number of them, and 48 latkes go on as 8 of the
  // item's size holding 6.
  //
  // The latkes are photos (assets/latke-box-latke-1..4.webp, passed in as
  // data-latke-images) drawn on a <canvas> over a small rigid-body
  // simulation, no library. It is 2.5D:
  //
  //   - Each latke is a capsule (a segment with round ends) in its own
  //     upright plane, which matches the photos' long oval shape well enough
  //     that they lie flat, stack and slide off each other. It tumbles about
  //     the depth axis only.
  //   - Each latke also has a depth: how far forward of the box's back wall
  //     it is. Two latkes only touch when they overlap in depth, and one
  //     resting off-centre (in depth) on another slides off it forward or
  //     back, so the pile spreads over the whole floor of the box.
  //   - The world is drawn in the photo's own perspective: its vanishing
  //     point and the back edge of its floor fix how big a latke looks and
  //     where the floor is at each depth.
  //
  // Contacts are solved with sequential impulses (accumulated, with
  // restitution and Coulomb friction), overlap is removed by positional
  // correction, and bodies sleep, so the animation loop stops once the pile
  // is at rest. A sleeping latke acts as fixed scenery until something lands
  // on it hard enough to wake it.
  //
  // With prefers-reduced-motion the latkes are laid out directly as a settled
  // pile and nothing animates.
  //
  // Jake stands in the box (see JAKE_VH): a fixed body, never moved or
  // woken, that latkes collide with like any other.

  // ─── Tuning ─────────────────────────────────────────────────────────────

  var STEP = 1 / 120;           // fixed physics step, seconds
  var MAX_STEPS_PER_FRAME = 6;  // after a stall, drop time rather than catch up
  var ITERATIONS = 10;          // velocity solver passes per step
  var RESTITUTION = 0.18;       // latke on latke
  var FLOOR_RESTITUTION = 0.12;
  var BOUNCE_MIN_SPEED = 0.25;  // in latke lengths/s: slower impacts do not bounce
  var WAKE_SPEED = 0.6;         // in latke lengths/s: a hit this hard wakes a sleeper
  var FRICTION = 0.6;
  var SLOP = 0.4;               // px of overlap tolerated before correction
  var CORRECTION = 0.6;         // share of the overlap removed per pass
  var CONTACT_MARGIN = 0.5;     // px: this close counts as touching
  var TOUCH_DAMPING = 1.6;      // 1/s, angular damping while touching something
  var AIR_DAMPING = 0.05;       // 1/s
  var DEPTH_DAMPING = 5;        // 1/s, depth motion lost to friction while touching
  var INCLINE = 0.6;            // depth push off a latke underneath, per g, at full offset
  var INCLINE_MIN = 0.35;       // depth offsets below this share of a latke grip (no slide)
  // Hysteresis, so nothing flickers between two states every frame:
  var INCLINE_RELEASE = 0.2;    // a sliding latke keeps sliding down to this offset
  var DEPTH_RELEASE = 1.12;     // two latkes that touch stay touching until this
                                // far apart in depth (in latke depths); they
                                // only start touching under 1
  var MAX_SPIN = 24;            // rad/s
  // A real latke is a disc: tilted up on its edge it falls over sideways in
  // depth, which a latke turning only about the depth axis cannot do. This
  // turns a touching latke toward lying flat instead, strongest at 45°.
  var TIP = 8;                  // rad/s^2
  var SLEEP_TIME = 0.35;        // s of stillness before a latke sleeps
  var PINNED_TIME = 0.8;        // s without real movement before a latke sleeps
  var PINNED_UNSTABLE_TIME = 2.5; // the same, for one with a single support:
                                  // a latke really tipping over moves by then
  var FORCE_SLEEP_AGE = 5;      // s awake before a pinned, unsupported latke sleeps
  var MAX_AWAKE_AGE = 8;        // s: past this, sleep no matter what

  // A latke's collision shape, in latke lengths (L = the photo's drawn width
  // at the back wall): a capsule CAP_R thick on each side of a segment
  // 2 * CAP_H long, so 2 * (CAP_H + CAP_R) = 0.96 L end to end, slightly
  // inside the photo so neighbours nestle rather than leave gaps; and DEPTH
  // deep, front to back.
  var CAP_R = 0.22;
  var CAP_H = 0.26;
  var DEPTH = 0.55;
  // Moment of inertia per unit mass, in L^2 (a rectangle of the same size).
  var CAP_INERTIA = (Math.pow(2 * (CAP_H + CAP_R), 2) + Math.pow(2 * CAP_R, 2)) / 12;

  // Sizing. A latke's size never changes with the count; it has two sizes,
  // one for the slider and a bigger one for the set order sizes. On the
  // slider it is the size at which max_latkes of them fill the box up to the
  // top of the card (fitLength()), so every latke the slider can ask for is
  // in the box; with the set sizes it is the size at which the largest set
  // size fills it. Either is at most as big as the "large" size the page once
  // used (baseLength): SIZE_FACTOR times the first version's round latkes,
  // which had radius r, sized so that OLD_MAX of them filled OLD_FILL of an
  // area REF_FREE of the screen tall, measured where that version's floor
  // was (REF_V down the photo); 2.3 r was their drawn width.
  var OLD_FILL = 0.6;
  var OLD_MAX = 100;
  var REF_FREE = 0.44;
  var SIZE_FACTOR = 4;
  var REF_V = 0.9;
  var FIT_FILL = 0.8;           // share of the box a full pile may fill
  var PACKING = 0.4;            // how densely a tumbled pile of latkes packs
  var MIN_LENGTH = 36;          // px
  var RESIZE_RATE = 7;          // switching sizes eases ~1/7 s per e-fold

  // The box photo (assets/latke-box-bg.webp), drawn by CSS as `cover`,
  // anchored at its bottom centre. Measured off the photo, as a share of its
  // width (u) or height (v): the vanishing point, the back edge of the floor,
  // and the back wall's two bottom corners. Replacing the photo means
  // re-measuring these.
  var BG_W = 2048;
  var BG_H = 1222;
  var VP_U = 0.473;
  var VP_V = 0.343;
  var BACK_FLOOR_V = 0.767;
  var BACK_WALL_U_L = 0.1875;
  var BACK_WALL_U_R = 0.8;
  // How deep the box is: the camera's distance from the back wall, in photo
  // widths. 1.2 makes the floor (to the photo's bottom edge) about 70% as
  // deep as the box is wide.
  var CAMERA_DISTANCE = 1.2;
  var FRONT_MARGIN = 0.05;      // share of the screen's height kept clear at the bottom
  var EDGE = 4;                 // px kept clear of the screen's sides

  // Jake (assets/latke-box-jake.webp, passed in as data-jake-image) stands on
  // the floor, centred, JAKE_FRONT of the way from the back of the box to
  // the front, JAKE_VH of the window's height tall, and is solid: latkes land on him and pile up around him. His collision
  // shape is an upright capsule JAKE_R of his height thick on each side and
  // JAKE_DEPTH of it deep.
  var JAKE_VH = 0.45;
  var JAKE_FRONT = 1;
  var JAKE_R = 0.16;
  var JAKE_DEPTH = 0.22;

  var reduceMotionQuery = window.matchMedia
    ? window.matchMedia('(prefers-reduced-motion: reduce)')
    : null;

  // ─── Small helpers ──────────────────────────────────────────────────────

  function clamp(v, lo, hi) {
    return v < lo ? lo : v > hi ? hi : v;
  }

  // A soft round blob used for every shadow, scaled at draw time.
  function renderShadowSprite() {
    var c = document.createElement('canvas');
    c.width = c.height = 64;
    var g = c.getContext('2d');
    var grad = g.createRadialGradient(32, 32, 0, 32, 32, 32);
    grad.addColorStop(0, 'rgba(38,20,5,1)');
    grad.addColorStop(0.55, 'rgba(38,20,5,0.75)');
    grad.addColorStop(1, 'rgba(38,20,5,0)');
    g.fillStyle = grad;
    g.fillRect(0, 0, 64, 64);
    return c;
  }

  // A latke photo scaled down once, in halving steps, to the largest size it
  // is drawn at (the front of the box), so the canvas never resamples a
  // 640px photo per latke per frame and Safari, which ignores
  // imageSmoothingQuality, does not alias it.
  function renderPhotoSprite(img, width, height) {
    var src = img;
    var sw = img.naturalWidth;
    var sh = img.naturalHeight;
    while (sw / 2 > width) {
      var half = document.createElement('canvas');
      half.width = Math.max(1, Math.round(sw / 2));
      half.height = Math.max(1, Math.round(sh / 2));
      var hg = half.getContext('2d');
      hg.imageSmoothingEnabled = true;
      hg.imageSmoothingQuality = 'high';
      hg.drawImage(src, 0, 0, half.width, half.height);
      src = half;
      sw = half.width;
      sh = half.height;
    }
    var c = document.createElement('canvas');
    c.width = Math.max(1, Math.round(width));
    c.height = Math.max(1, Math.round(height));
    var g = c.getContext('2d');
    g.imageSmoothingEnabled = true;
    g.imageSmoothingQuality = 'high';
    g.drawImage(src, 0, 0, c.width, c.height);
    return c;
  }

  // Closest point on segment (ax,ay)-(bx,by) to (px,py), as its parameter t.
  function segParam(px, py, ax, ay, bx, by) {
    var dx = bx - ax;
    var dy = by - ay;
    var len2 = dx * dx + dy * dy;
    if (len2 < 1e-9) return 0;
    return clamp(((px - ax) * dx + (py - ay) * dy) / len2, 0, 1);
  }

  function cross(ax, ay, bx, by) {
    return ax * by - ay * bx;
  }

  // ─── One box ────────────────────────────────────────────────────────────

  function init(root) {
    if (!root || root.__latkeBox) return;

    var canvas = root.querySelector('.latke-box__canvas');
    var uiEl = root.querySelector('.latke-box__ui');
    var cardEl = root.querySelector('.latke-box__card');
    var input = root.querySelector('[data-latke-qty]');
    var slider = root.querySelector('[data-latke-slider]');
    var statusEl = root.querySelector('[data-latke-status]');
    var cta = root.querySelector('[data-latke-cta]');
    // The set sizes, and the slider and field behind "Custom size". Without
    // set sizes there's no switch and the slider is always shown.
    var sizesEl = root.querySelector('[data-latke-sizes]');
    var sizeButtons = root.querySelectorAll('[data-latke-size]');
    var customBtn = root.querySelector('[data-latke-custom]');
    var pickerEl = root.querySelector('[data-latke-picker]');
    var backBtn = root.querySelector('[data-latke-back]');
    var custom = !sizesEl;  // the slider is showing
    if (!canvas || !canvas.getContext || !input || !slider) return;

    var ctx = canvas.getContext('2d');
    var pack = Math.max(1, parseInt(root.getAttribute('data-pack'), 10) || 1);
    var max = Math.max(1, parseInt(root.getAttribute('data-max'), 10) || 100);
    max = Math.max(pack, Math.floor(max / pack) * pack);
    var presetMax = 0;
    for (var si = 0; si < sizeButtons.length; si++) {
      presetMax = Math.max(presetMax, parseInt(sizeButtons[si].getAttribute('data-latke-size'), 10) || 0);
    }
    presetMax = Math.min(presetMax, max);
    var startCount = toPacks(parseInt(root.getAttribute('data-start'), 10) || pack);
    var handle = root.getAttribute('data-latke-handle') || 'latkes';
    var sidesUrl = root.getAttribute('data-sides-url') || '/pages/chanukkah';
    var announceOne = root.getAttribute('data-announce-one') || '1 latke';
    var announceMany = root.getAttribute('data-announce-many') || '[count] latkes';

    var shadowSprite = renderShadowSprite();

    // The latke photos. Until one has loaded, its latkes are drawn as plain
    // golden ovals; each load redraws the box.
    var photos = [];
    (root.getAttribute('data-latke-images') || '').split('|').forEach(function (url) {
      url = url.trim();
      if (!url) return;
      var img = new Image();
      img.decoding = 'async';
      img.onload = function () {
        spriteCache = [];
        draw();
      };
      img.src = url;
      photos.push(img);
    });
    var spriteCache = [];   // per photo: { canvas, width, dpr }

    var jakeUrl = root.getAttribute('data-jake-image') || '';
    var jakeImg = null;
    var jakeSprite = null;  // { canvas, width, dpr }
    var jake = null;        // his fixed body, placed by placeJake()
    if (jakeUrl) {
      jakeImg = new Image();
      jakeImg.decoding = 'async';
      jakeImg.onload = function () {
        jakeSprite = null;
        draw();
      };
      jakeImg.src = jakeUrl;
    }
    var nextPhoto = Math.floor(Math.random() * Math.max(1, photos.length));

    // ── The world ──
    // World units are screen px at the back wall. x runs across the box,
    // 0 below the vanishing point; y runs down, the floor at 0 (latkes sit
    // at y < 0); p is depth, forward from the back wall. A point at depth p
    // is drawn s(p) = F / (F - p) times bigger than at the back wall.
    var W = 0;
    var H = 0;
    var dpr = 1;
    var imgW = 0;           // the photo's drawn width, px
    var vpX = 0;            // vanishing point, screen px
    var vpY = 0;
    var floorBackY = 0;     // the floor's back edge, screen px
    var camF = 1;           // F, the camera's distance from the back wall
    var boxL = 0;           // the box's side walls, world x
    var boxR = 0;
    var pFloor = 0;         // the deepest a latke may come forward, before its own depth
    var sFront = 1;
    var sRef = 1;
    var fillTop = 0;        // screen y of the top of the card: a full box reaches it
    var cap = 1;            // how many latkes fit (capacity())
    var baseLength = 120;   // a latke's length while few enough fit
    var smallLength = 0;    // on the slider
    var bigLength = 0;      // with the set sizes
    var targetLength = 0;   // where `length` is easing to
    var length = 120;       // a latke's length now
    var capR = 0;           // collision shape, world px (from length)
    var capH = 0;
    var depthT = 0;
    var pMin = 0;
    var pMax = 0;
    var invI = 1;
    var gravity = 2000;

    var bodies = [];        // in the order they were added
    var count = 1;          // what the customer asked for
    var reduced = !!(reduceMotionQuery && reduceMotionQuery.matches);

    var rafId = 0;
    var lastTime = 0;
    var accumulator = 0;
    var pumpTimer = 0;
    var pumpDelay = 80;
    var announceTimer = 0;
    var resizeRaf = 0;
    var resizeObserver = null;

    // The nearest whole number of packs, within the slider's range.
    function toPacks(n) {
      return clamp(Math.round(n / pack) * pack, pack, max);
    }

    function scaleAt(p) {
      return camF / Math.max(1, camF - p);
    }

    function floorScreenY(s) {
      return vpY + (floorBackY - vpY) * s;
    }

    // The side walls at depth p: the box's own, or the screen's edge where
    // the photo is cropped (phones), whichever is nearer.
    function wallLAt(p) {
      return Math.max(boxL, (EDGE - vpX) / scaleAt(p));
    }

    function wallRAt(p) {
      return Math.min(boxR, (W - EDGE - vpX) / scaleAt(p));
    }

    // ── Bodies ──

    function pickPhoto() {
      var n = photos.length || 1;
      // Never the same photo twice in a row.
      nextPhoto = (nextPhoto + 1 + Math.floor(Math.random() * Math.max(1, n - 1))) % n;
      return nextPhoto;
    }

    var bodyIds = 0;
    var stepNo = 0;         // physics steps so far; stamps who touched whom

    // A body's capsule radius, half segment and depth: Jake's own, or the
    // latkes' shared ones.
    function rOf(b) {
      return b.fixed ? b.r : capR;
    }

    function hlOf(b) {
      return b.fixed ? b.hl : capH;
    }

    function dtOf(b) {
      return b.fixed ? b.dt : depthT;
    }

    // Jake on the floor, centred, towards the front. Id 0, below every
    // latke's.
    function placeJake() {
      if (!jakeUrl) {
        jake = null;
        return;
      }
      // Where the slider-size latkes' depth range puts him, so he stays put
      // when the latkes change size.
      var front = DEPTH * smallLength;
      var lo = front * 0.5;
      var p = lo + JAKE_FRONT * (Math.max(lo, pFloor - front * 0.2) - lo);
      var s = scaleAt(p);
      var h = (JAKE_VH * (window.innerHeight || H)) / s;
      var r = JAKE_R * h;
      var x = (wallLAt(p) + wallRAt(p)) / 2;
      jake = {
        fixed: true, awake: false, id: 0, touch: {},
        x: x, y: -h / 2, p: p, a: Math.PI / 2, vx: 0, vy: 0, vp: 0, w: 0,
        h: h, r: r, hl: h / 2 - r, dt: JAKE_DEPTH * h,
        e0x: x, e0y: -h + r, e1x: x, e1y: -r,
        supports: 0, contacts: 0, slideF: 0, slideB: 0
      };
    }

    function makeBody(x, y) {
      return {
        x: x, y: y, p: 0, vx: 0, vy: 0, vp: 0,
        a: 0, w: 0,
        photo: pickPhoto(),
        flip: Math.random() < 0.5 ? -1 : 1,
        e0x: 0, e0y: 0, e1x: 0, e1y: 0,
        awake: true, still: 0, age: 0, contacts: 0, supports: 0,
        slideF: 0, slideB: 0, sliding: false,
        id: ++bodyIds,
        touch: {},          // other latke's id -> step they last touched (DEPTH_RELEASE)
        ax: x, ay: y, ap: 0, pinned: 0,
        removing: false, removeT: 0
      };
    }

    // The two ends of a latke's segment.
    function updateEnds(b) {
      var ex = Math.cos(b.a) * capH;
      var ey = Math.sin(b.a) * capH;
      b.e0x = b.x - ex;
      b.e0y = b.y - ey;
      b.e1x = b.x + ex;
      b.e1y = b.y + ey;
    }

    // How far a latke reaches from its centre, sideways and downward.
    function extentX(b) {
      return Math.abs(Math.cos(b.a)) * capH + capR;
    }

    function extentY(b) {
      return Math.abs(Math.sin(b.a)) * capH + capR;
    }

    function liveCount() {
      var n = 0;
      for (var i = 0; i < bodies.length; i++) if (!bodies[i].removing) n++;
      return n;
    }

    function wake(b) {
      if (b.fixed) return;
      if (!b.awake) {
        b.awake = true;
        b.still = 0;
        b.age = 0;
        b.pinned = 0;
        b.ax = b.x;
        b.ay = b.y;
        b.ap = b.p;
      }
    }

    function wakeAll() {
      for (var i = 0; i < bodies.length; i++) wake(bodies[i]);
    }

    function spawn() {
      var b = makeBody(0, 0);
      b.p = pMin + Math.random() * (pMax - pMin);
      var s = scaleAt(b.p);
      var reach = (CAP_H + CAP_R) * length;
      var wl = wallLAt(b.p);
      var span = Math.max(0, wallRAt(b.p) - wl - 2 * reach);
      b.x = wl + reach + Math.random() * span;
      // Just above the top of the screen.
      var screenY = -length * s * (0.5 + Math.random() * 1.2);
      b.y = (screenY - floorScreenY(s)) / s;
      b.a = (Math.random() - 0.5) * Math.PI * 0.9;
      b.vx = (Math.random() - 0.5) * length * 1.2;
      b.vy = length * 1.5;
      b.vp = (Math.random() - 0.5) * length * 0.4;
      b.w = (Math.random() - 0.5) * 6;
      b.ax = b.x;
      b.ay = b.y;
      b.ap = b.p;
      updateEnds(b);
      bodies.push(b);
      start();
    }

    function removeLatest() {
      for (var i = bodies.length - 1; i >= 0; i--) {
        if (!bodies[i].removing) {
          bodies[i].removing = true;
          bodies[i].removeT = 0;
          // Whatever rested on it now has nothing underneath.
          wakeAll();
          start();
          return;
        }
      }
    }

    // How many latkes are in the box: the count, up to what fits.
    function shown() {
      return Math.min(count, cap);
    }

    // Bring the box to shown(), one latke at a time with a short stagger so
    // a typed number rains in instead of arriving as a lump. (With reduced
    // motion, syncBodies lays the pile out at once instead.)
    function pump() {
      pumpTimer = 0;
      var live = liveCount();
      var want = shown();
      if (live < want) spawn();
      else if (live > want) removeLatest();
      if (liveCount() !== shown()) pumpTimer = window.setTimeout(pump, pumpDelay);
    }

    function syncBodies() {
      window.clearTimeout(pumpTimer);
      pumpTimer = 0;
      var want = shown();
      if (reduced) {
        while (liveCount() < want) bodies.push(makeBody(0, 0));
        while (liveCount() > want) bodies.pop();
        layoutPile();
        draw();
        return;
      }
      var diff = Math.abs(want - liveCount());
      if (!diff) return;
      pumpDelay = clamp(1400 / diff, 28, 110);
      pump();
    }

    // ── Settled pile (reduced motion) ──
    // Latkes lying flat in a grid across the floor, filled from the middle
    // outward, then a second layer on the first, and so on.
    function layoutPile() {
      var live = [];
      for (var i = 0; i < bodies.length; i++) if (!bodies[i].removing) live.push(bodies[i]);
      var n = live.length;
      if (!n) return;
      var pitchX = 2 * (capH + capR) * 1.03;
      var pitchP = depthT * 1.05;
      var rows = Math.max(1, Math.floor((pMax - pMin) / pitchP) + 1);
      var slots = [];
      for (var r = 0; r < rows; r++) {
        var p = rows === 1 ? (pMin + pMax) / 2 : pMin + r * ((pMax - pMin) / (rows - 1));
        var wl = wallLAt(p);
        var wr = wallRAt(p);
        var cols = Math.max(1, Math.floor((wr - wl) / pitchX));
        for (var c = 0; c < cols; c++) {
          var x = (wl + wr) / 2 + (c - (cols - 1) / 2) * pitchX;
          // Not where Jake stands.
          if (jake && Math.abs(p - jake.p) < (depthT + jake.dt) / 2 &&
              Math.abs(x - jake.x) < jake.r + capH + capR) continue;
          slots.push({
            x: x,
            p: p,
            d: Math.abs(x - (wl + wr) / 2) / (wr - wl) + Math.abs(p - (pMin + pMax) / 2) / Math.max(1, pMax - pMin)
          });
        }
      }
      slots.sort(function (s1, s2) { return s1.d - s2.d; });
      for (i = 0; i < n; i++) {
        var slot = slots[i % slots.length];
        var layer = Math.floor(i / slots.length);
        var b = live[i];
        b.x = slot.x;
        b.p = slot.p;
        b.y = -capR - layer * 2 * capR;
        b.a = Math.random() < 0.5 ? 0 : Math.PI;
        b.vx = b.vy = b.vp = b.w = 0;
        b.awake = false;
        updateEnds(b);
      }
    }

    // ── Contacts ──
    // A contact pushes `a` and `b` apart along (nx, ny), which points from a
    // to b; for a wall or the floor, b is null and the normal points from the
    // latke into the wall. Contacts are pooled and reused every step.

    var contacts = [];
    var contactCount = 0;

    function addContact(a, b, nx, ny, px, py, pen) {
      var c = contacts[contactCount];
      if (!c) {
        c = {};
        contacts[contactCount] = c;
      }
      contactCount++;
      c.a = a;
      c.b = b;
      c.nx = nx;
      c.ny = ny;
      c.rax = px - a.x;
      c.ray = py - a.y;
      c.rbx = b ? px - b.x : 0;
      c.rby = b ? py - b.y : 0;
      c.pen = pen;
      c.jn = 0;
      c.jt = 0;
      c.floor = false;
      return c;
    }

    // Candidate closest-point pairs between two latkes, reused per pair.
    var cand = [];
    for (var ci = 0; ci < 5; ci++) cand.push({ ax: 0, ay: 0, bx: 0, by: 0, d: 0, nx: 0, ny: 0 });
    var candCount = 0;

    function addCand(ax, ay, bx, by, a, b) {
      var c = cand[candCount++];
      c.ax = ax;
      c.ay = ay;
      c.bx = bx;
      c.by = by;
      var dx = bx - ax;
      var dy = by - ay;
      var d = Math.sqrt(dx * dx + dy * dy);
      c.d = d;
      if (d > 1e-6) {
        c.nx = dx / d;
        c.ny = dy / d;
      } else {
        dx = b.x - a.x;
        dy = b.y - a.y;
        d = Math.sqrt(dx * dx + dy * dy) || 1;
        c.nx = dx / d;
        c.ny = dy / d;
      }
    }

    function endVsSegment(px, py, s, a, b, pointOnA) {
      var t = segParam(px, py, s.e0x, s.e0y, s.e1x, s.e1y);
      var qx = s.e0x + (s.e1x - s.e0x) * t;
      var qy = s.e0y + (s.e1y - s.e0y) * t;
      if (pointOnA) addCand(px, py, qx, qy, a, b);
      else addCand(qx, qy, px, py, a, b);
    }

    // Up to two contacts between latkes a and b, if they overlap in depth.
    // In 2D, two segments that do not cross are closest at an end of one of
    // them, so the four end-to-segment tests find every touching point; two
    // of them (the deepest and the one farthest from it) let a latke lie
    // flat on another without rocking. Crossed segments, which only happen
    // after a deep overlap, are pushed apart sideways.
    function pairContacts(a, b) {
      var dp = Math.abs(a.p - b.p);
      var dlim = (dtOf(a) + dtOf(b)) / 2;
      if (dp >= dlim * DEPTH_RELEASE) return 0;
      var dx = b.x - a.x;
      var dy = b.y - a.y;
      var reach = hlOf(a) + rOf(a) + hlOf(b) + rOf(b) + CONTACT_MARGIN;
      if (dx * dx + dy * dy > reach * reach) return 0;
      // Touching last step (or earlier this one): they stay in touch out to
      // DEPTH_RELEASE; otherwise they only meet within one latke depth.
      var owner = a.id < b.id ? a : b;
      var other = owner === a ? b : a;
      var last = owner.touch[other.id];
      var engaged = last !== undefined && last >= stepNo - 1;
      if (!engaged && dp >= dlim) return 0;
      var rr = rOf(a) + rOf(b);
      var touch = rr + CONTACT_MARGIN;

      candCount = 0;
      endVsSegment(a.e0x, a.e0y, b, a, b, true);
      endVsSegment(a.e1x, a.e1y, b, a, b, true);
      endVsSegment(b.e0x, b.e0y, a, a, b, false);
      endVsSegment(b.e1x, b.e1y, a, a, b, false);

      // Crossed segments?
      var d1 = cross(a.e1x - a.e0x, a.e1y - a.e0y, b.e0x - a.e0x, b.e0y - a.e0y);
      var d2 = cross(a.e1x - a.e0x, a.e1y - a.e0y, b.e1x - a.e0x, b.e1y - a.e0y);
      var d3 = cross(b.e1x - b.e0x, b.e1y - b.e0y, a.e0x - b.e0x, a.e0y - b.e0y);
      var d4 = cross(b.e1x - b.e0x, b.e1y - b.e0y, a.e1x - b.e0x, a.e1y - b.e0y);
      if (d1 * d2 < 0 && d3 * d4 < 0) {
        var t = d1 / (d1 - d2);
        var ix = b.e0x + (b.e1x - b.e0x) * t;
        var iy = b.e0y + (b.e1y - b.e0y) * t;
        var c = cand[candCount++];
        c.ax = c.bx = ix;
        c.ay = c.by = iy;
        c.d = 0;
        // Out along a's perpendicular, on b's side.
        var px = -Math.sin(a.a);
        var py = Math.cos(a.a);
        if (px * dx + py * dy < 0) {
          px = -px;
          py = -py;
        }
        c.nx = px;
        c.ny = py;
      }

      var best = -1;
      for (var i = 0; i < candCount; i++) {
        if (cand[i].d < touch && (best < 0 || cand[i].d < cand[best].d)) best = i;
      }
      if (best < 0) return 0;
      var bc = cand[best];

      // Two latkes meeting edge-on in depth (sliding past each other while
      // they overlap a lot on screen) are parted the short way, in depth,
      // rather than shoved apart sideways, which the rest of the pile would
      // shove straight back: a jitter. Only for a pair not already touching.
      var depthPen = dlim - dp;
      if (!engaged && rr - bc.d > depthPen) {
        partInDepth(a, b, depthPen);
        return 0;
      }
      owner.touch[other.id] = stepNo;
      var bmx = (bc.ax + bc.bx) / 2;
      var bmy = (bc.ay + bc.by) / 2;
      var second = -1;
      var far = capR * capR * 0.25;
      for (i = 0; i < candCount; i++) {
        if (i === best || cand[i].d >= touch) continue;
        var mx = (cand[i].ax + cand[i].bx) / 2 - bmx;
        var my = (cand[i].ay + cand[i].by) / 2 - bmy;
        var m2 = mx * mx + my * my;
        if (m2 > far) {
          far = m2;
          second = i;
        }
      }
      addContact(a, b, bc.nx, bc.ny, bmx, bmy, rr - bc.d);
      if (second >= 0) {
        var sc = cand[second];
        addContact(a, b, sc.nx, sc.ny, (sc.ax + sc.bx) / 2, (sc.ay + sc.by) / 2, rr - sc.d);
      }
      return 1;
    }

    // Move a and b apart in depth by `by` (sleepers and Jake stay put) and
    // stop them closing on each other in depth.
    function partInDepth(a, b, by) {
      var ia = a.awake ? 1 : 0;
      var ib = b.awake ? 1 : 0;
      if (!(ia + ib)) return;
      var dir = b.p >= a.p ? 1 : -1;
      var push = by / (ia + ib);
      a.p -= dir * push * ia;
      b.p += dir * push * ib;
      if ((b.vp - a.vp) * dir < 0) {
        if (ia && ib) a.vp = b.vp = (a.vp + b.vp) / 2;
        else if (ia) a.vp = b.vp;
        else b.vp = a.vp;
      }
    }

    function boxContacts(b) {
      var wl = wallLAt(b.p);
      var wr = wallRAt(b.p);
      var ends = [b.e0x, b.e0y, b.e1x, b.e1y];
      for (var i = 0; i < 4; i += 2) {
        var ex = ends[i];
        var ey = ends[i + 1];
        var pen = ey + capR;
        if (pen > -CONTACT_MARGIN) addContact(b, null, 0, 1, ex, 0, pen).floor = true;
        pen = wl - (ex - capR);
        if (pen > -CONTACT_MARGIN) addContact(b, null, -1, 0, wl, ey, pen);
        pen = ex + capR - wr;
        if (pen > -CONTACT_MARGIN) addContact(b, null, 1, 0, wr, ey, pen);
      }
    }

    // A latke resting on another that is offset in depth slides off it,
    // forward or back, faster the further off-centre it sits. This only
    // notes the offsets; step() applies them, and not at all to a latke
    // held from both sides (resting in the dip between one in front and
    // one behind), which would otherwise rock between them for ever.
    function incline(c) {
      var upper;
      var lower;
      if (c.ny > 0.5) {
        upper = c.a;
        lower = c.b;
      } else if (c.ny < -0.5) {
        upper = c.b;
        lower = c.a;
      } else {
        return;
      }
      if (!upper.awake) return;
      var off = (upper.p - lower.p) / ((dtOf(upper) + dtOf(lower)) / 2);
      var min = upper.sliding ? INCLINE_RELEASE : INCLINE_MIN;
      if (off > min) upper.slideF = Math.max(upper.slideF, off);
      else if (off < -min) upper.slideB = Math.min(upper.slideB, off);
    }

    // ── Solver ──

    function prepare(c) {
      var a = c.a;
      var b = c.b;
      var rvx = -(a.vx - a.w * c.ray);
      var rvy = -(a.vy + a.w * c.rax);
      if (b) {
        rvx += b.vx - b.w * c.rby;
        rvy += b.vy + b.w * c.rbx;
      }
      var vn = rvx * c.nx + rvy * c.ny;
      // A hard enough hit wakes a sleeper; a gentle one treats it as scenery.
      if (b && -vn > WAKE_SPEED * length) {
        wake(a);
        wake(b);
      }
      c.ima = a.awake ? 1 : 0;
      c.iia = a.awake ? invI : 0;
      c.imb = b && b.awake ? 1 : 0;
      c.iib = b && b.awake ? invI : 0;
      var ran = cross(c.rax, c.ray, c.nx, c.ny);
      var rbn = cross(c.rbx, c.rby, c.nx, c.ny);
      var kn = c.ima + c.imb + c.iia * ran * ran + c.iib * rbn * rbn;
      c.massN = kn > 0 ? 1 / kn : 0;
      var tx = -c.ny;
      var ty = c.nx;
      var rat = cross(c.rax, c.ray, tx, ty);
      var rbt = cross(c.rbx, c.rby, tx, ty);
      var kt = c.ima + c.imb + c.iia * rat * rat + c.iib * rbt * rbt;
      c.massT = kt > 0 ? 1 / kt : 0;
      var e = b ? RESTITUTION : (c.floor ? FLOOR_RESTITUTION : RESTITUTION);
      c.target = -vn > BOUNCE_MIN_SPEED * length ? -e * vn : 0;
    }

    function applyImpulse(c, px, py) {
      var a = c.a;
      var b = c.b;
      a.vx -= px * c.ima;
      a.vy -= py * c.ima;
      a.w -= c.iia * cross(c.rax, c.ray, px, py);
      if (b) {
        b.vx += px * c.imb;
        b.vy += py * c.imb;
        b.w += c.iib * cross(c.rbx, c.rby, px, py);
      }
    }

    function solveContact(c) {
      if (!c.massN) return;
      var a = c.a;
      var b = c.b;
      var rvx = -(a.vx - a.w * c.ray);
      var rvy = -(a.vy + a.w * c.rax);
      if (b) {
        rvx += b.vx - b.w * c.rby;
        rvy += b.vy + b.w * c.rbx;
      }

      // Normal: never pull, push until the target separation speed.
      var vn = rvx * c.nx + rvy * c.ny;
      var jn = Math.max(0, c.jn + (c.target - vn) * c.massN);
      var dj = jn - c.jn;
      c.jn = jn;
      applyImpulse(c, dj * c.nx, dj * c.ny);

      // Friction along the tangent, inside Coulomb's cone. This is what
      // turns a glancing landing into spin and lets a latke grip the pile.
      rvx = -(a.vx - a.w * c.ray);
      rvy = -(a.vy + a.w * c.rax);
      if (b) {
        rvx += b.vx - b.w * c.rby;
        rvy += b.vy + b.w * c.rbx;
      }
      var tx = -c.ny;
      var ty = c.nx;
      var vt = rvx * tx + rvy * ty;
      var lim = FRICTION * c.jn;
      var jt = clamp(c.jt - vt * c.massT, -lim, lim);
      dj = jt - c.jt;
      c.jt = jt;
      applyImpulse(c, dj * tx, dj * ty);
    }

    // Keep a latke inside the box. Pushing it back also stops any motion
    // into the wall, so a latke wedged against a wall cannot keep pushing
    // into it forever.
    function clampToBox(b) {
      if (b.p < pMin) {
        b.p = pMin;
        if (b.vp < 0) b.vp = 0;
      }
      if (b.p > pMax) {
        b.p = pMax;
        if (b.vp > 0) b.vp = 0;
      }
      var ey = extentY(b);
      var ex = extentX(b);
      var wl = wallLAt(b.p);
      var wr = wallRAt(b.p);
      if (b.y > -ey) {
        b.y = -ey;
        if (b.vy > 0) b.vy = 0;
      }
      if (b.x < wl + ex) {
        b.x = wl + ex;
        if (b.vx < 0) b.vx = 0;
      }
      if (b.x > wr - ex) {
        b.x = wr - ex;
        if (b.vx > 0) b.vx = 0;
      }
      updateEnds(b);
    }

    function step(h) {
      var i, j, b, it;
      stepNo++;
      var active = [];
      for (i = 0; i < bodies.length; i++) if (!bodies[i].removing) active.push(bodies[i]);
      var n = active.length;
      var maxSpeed = (capR * 0.8) / h; // never move further than this per step

      for (i = 0; i < n; i++) {
        b = active[i];
        updateEnds(b);
        b.slideF = b.slideB = 0;
        if (b.awake) b.vy += gravity * h;
      }

      contactCount = 0;
      for (i = 0; i < n; i++) {
        var a = active[i];
        for (j = i + 1; j < n; j++) {
          if (!a.awake && !active[j].awake) continue;
          var first = contactCount;
          if (pairContacts(a, active[j])) incline(contacts[first]);
        }
        if (a.awake) {
          boxContacts(a);
          if (jake) {
            var jf = contactCount;
            if (pairContacts(a, jake)) incline(contacts[jf]);
          }
        }
      }
      for (i = 0; i < n; i++) {
        b = active[i];
        var bridged = b.slideF > 0 && b.slideB < 0;
        b.sliding = !bridged && (b.slideF > 0 || b.slideB < 0);
        if (b.awake && b.sliding) {
          b.vp += INCLINE * gravity * (b.slideF + b.slideB) * h;
        }
      }
      // Count what holds each latke up: points of the floor, or of a latke
      // beside or underneath it (one pressing down from above does not
      // count). One point is a balancing act (a latke on its end, or on a
      // single crest), not rest; it may not sleep on that.
      for (i = 0; i < n; i++) active[i].supports = 0;
      for (i = 0; i < contactCount; i++) {
        var sc = contacts[i];
        if (!sc.b) {
          if (sc.floor) sc.a.supports++;
        } else {
          if (sc.ny > -0.2) sc.a.supports++;
          if (sc.ny < 0.2) sc.b.supports++;
        }
      }
      for (i = 0; i < contactCount; i++) prepare(contacts[i]);
      // prepare() may have woken a sleeper after an earlier contact was
      // prepared with it as scenery; re-prepare so every contact agrees.
      for (i = 0; i < contactCount; i++) prepare(contacts[i]);
      for (it = 0; it < ITERATIONS; it++) {
        for (i = 0; i < contactCount; i++) solveContact(contacts[i]);
      }

      // The one rule that keeps the pile calm: a latke's centre never moves
      // up. No bounce, and no latke launched out of the pile by a crowded
      // landing; one that lands simply stops. (Only the floor and walls, in
      // clampToBox, lift a latke, when it tips into them; overlap correction
      // pushes sideways or down, never up.)
      for (i = 0; i < n; i++) {
        if (active[i].vy < 0) active[i].vy = 0;
      }

      for (i = 0; i < n; i++) {
        b = active[i];
        if (!b.awake) continue;
        var sp = Math.sqrt(b.vx * b.vx + b.vy * b.vy);
        if (sp > maxSpeed) {
          b.vx *= maxSpeed / sp;
          b.vy *= maxSpeed / sp;
        }
        b.w = clamp(b.w, -MAX_SPIN, MAX_SPIN);
        b.x += b.vx * h;
        b.y += b.vy * h;
        b.p += b.vp * h;
        b.a += b.w * h;
        updateEnds(b);
      }

      // Push overlapping latkes apart (sleepers do not move), then keep
      // everyone inside the box. The first pass also counts who touches whom.
      for (i = 0; i < n; i++) active[i].contacts = 0;
      for (it = 0; it < 2; it++) {
        for (i = 0; i < n; i++) {
          for (j = i + 1; j < n; j++) {
            var p = active[i];
            var q = active[j];
            if (!p.awake && !q.awake) continue;
            var mark = contactCount;
            if (!pairContacts(p, q)) continue;
            if (it === 0) {
              p.contacts++;
              q.contacts++;
            }
            // Only the deepest point of the pair is corrected.
            var c = contacts[mark];
            contactCount = mark;
            var ia = p.awake ? 1 : 0;
            var ib = q.awake ? 1 : 0;
            if (c.pen <= SLOP) continue;
            var corr = ((c.pen - SLOP) * CORRECTION) / (ia + ib);
            p.x -= c.nx * corr * ia;
            q.x += c.nx * corr * ib;
            // Overlap is pushed apart sideways or down, never up (see the
            // rule after the solver).
            p.y += Math.max(0, -c.ny * corr * ia);
            q.y += Math.max(0, c.ny * corr * ib);
            updateEnds(p);
            updateEnds(q);
          }
          // Out of Jake, likewise (he never moves).
          var pj = active[i];
          if (jake && pj.awake) {
            var mj = contactCount;
            if (pairContacts(pj, jake)) {
              if (it === 0) pj.contacts++;
              var cj = contacts[mj];
              contactCount = mj;
              if (cj.pen > SLOP) {
                var cr = (cj.pen - SLOP) * CORRECTION;
                pj.x -= cj.nx * cr;
                pj.y += Math.max(0, -cj.ny * cr);
                updateEnds(pj);
              }
            }
          }
        }
      }

      var sleepSpeed = Math.max(6, capR * 0.5);
      for (i = 0; i < n; i++) {
        b = active[i];
        if (!b.awake) continue;
        clampToBox(b);
        var onFloor = b.y + extentY(b) >= -0.5;
        var ex = extentX(b);
        var onWall = b.x - ex <= wallLAt(b.p) + 0.5 || b.x + ex >= wallRAt(b.p) - 0.5;
        var touching = onFloor || onWall || b.contacts > 0;
        var stable = b.supports >= 2 || (onWall && b.supports >= 1);
        var damp = touching ? TOUCH_DAMPING : AIR_DAMPING;
        b.w *= Math.max(0, 1 - damp * h);
        if (touching) {
          b.w -= TIP * Math.sin(2 * b.a) * h;
          b.vx *= Math.max(0, 1 - 0.6 * h);
          b.vp *= Math.max(0, 1 - DEPTH_DAMPING * h);
        }
        b.age += h;
        var speed2 = b.vx * b.vx + b.vy * b.vy + b.vp * b.vp;
        if (stable && speed2 < sleepSpeed * sleepSpeed && Math.abs(b.w) < 0.35) {
          b.still += h;
        } else {
          b.still = 0;
        }
        // Velocity can lie when a latke is wedged (it pushes, nothing moves),
        // so also watch how far it has actually gone.
        var ddx = b.x - b.ax;
        var ddy = b.y - b.ay;
        var ddp = b.p - b.ap;
        var drift = capR * 0.25;
        if (ddx * ddx + ddy * ddy + ddp * ddp > drift * drift) {
          b.ax = b.x;
          b.ay = b.y;
          b.ap = b.p;
          b.pinned = 0;
        } else {
          b.pinned += h;
        }
        var pinned = b.pinned > PINNED_TIME &&
          (stable || b.pinned > PINNED_UNSTABLE_TIME || b.age > FORCE_SLEEP_AGE);
        if (b.still > SLEEP_TIME || pinned || b.age > MAX_AWAKE_AGE) {
          b.awake = false;
          b.vx = b.vy = b.vp = b.w = 0;
          b.still = 0;
          b.pinned = 0;
        }
      }
    }

    function anyMoving() {
      if (length !== targetLength) return true;
      for (var i = 0; i < bodies.length; i++) {
        if (bodies[i].awake || bodies[i].removing) return true;
      }
      return false;
    }

    function frame(t) {
      rafId = 0;
      var dt = lastTime ? (t - lastTime) / 1000 : STEP;
      lastTime = t;
      easeSize(dt);
      accumulator += Math.min(dt, STEP * MAX_STEPS_PER_FRAME);
      while (accumulator >= STEP) {
        step(STEP);
        accumulator -= STEP;
      }

      // Pop-out of removed latkes.
      for (var i = bodies.length - 1; i >= 0; i--) {
        var b = bodies[i];
        if (!b.removing) continue;
        b.removeT += dt / 0.22;
        if (b.removeT >= 1) bodies.splice(i, 1);
      }

      draw();
      if (anyMoving()) {
        rafId = window.requestAnimationFrame(frame);
      } else {
        lastTime = 0;
        accumulator = 0;
      }
    }

    function start() {
      if (reduced) {
        draw();
        return;
      }
      if (!rafId) {
        lastTime = 0;
        rafId = window.requestAnimationFrame(frame);
      }
    }

    // ── Rendering ──

    function photoReady(img) {
      return img && img.complete && img.naturalWidth > 0;
    }

    function photoAspect(k) {
      var img = photos[k];
      return photoReady(img) ? img.naturalWidth / img.naturalHeight : 2.15;
    }

    function spriteFor(k) {
      var img = photos[k];
      if (!photoReady(img)) return null;
      var width = targetLength * sFront;
      var s = spriteCache[k];
      if (!s || s.width !== width || s.dpr !== dpr) {
        s = {
          canvas: renderPhotoSprite(img, width * dpr, (width / photoAspect(k)) * dpr),
          width: width,
          dpr: dpr
        };
        spriteCache[k] = s;
      }
      return s.canvas;
    }

    // Back to front; at one depth, bottom to top. Jake by his feet.
    function drawKey(b) {
      return b.fixed ? b.p : b.p - b.y * 0.5;
    }

    function drawOrder(p, q) {
      return drawKey(p) - drawKey(q);
    }

    function drawJake() {
      if (!jakeImg || !jakeImg.complete || !jakeImg.naturalWidth) return;
      var s = scaleAt(jake.p);
      var hpx = jake.h * s;
      var wpx = hpx * (jakeImg.naturalWidth / jakeImg.naturalHeight);
      if (!jakeSprite || jakeSprite.width !== wpx || jakeSprite.dpr !== dpr) {
        jakeSprite = { canvas: renderPhotoSprite(jakeImg, wpx * dpr, hpx * dpr), width: wpx, dpr: dpr };
      }
      ctx.globalAlpha = 1;
      ctx.drawImage(jakeSprite.canvas, vpX + jake.x * s - wpx / 2, floorScreenY(s) - hpx, wpx, hpx);
    }

    function draw() {
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      var list = bodies.slice();
      if (jake) list.push(jake);
      list.sort(drawOrder);
      var L = length;
      var i, b, s, scale, alpha;

      // Shadows cast on the floor, fading as a latke is higher above it.
      for (i = 0; i < list.length; i++) {
        b = list[i];
        if (b.fixed) {
          s = scaleAt(b.p);
          ctx.globalAlpha = 0.4;
          ctx.drawImage(shadowSprite, vpX + b.x * s - b.h * s * 0.3, floorScreenY(s) - b.h * s * 0.07, b.h * s * 0.6, b.h * s * 0.13);
          continue;
        }
        var gap = -(b.y + extentY(b));
        if (gap > L * 1.5) continue;
        s = scaleAt(b.p);
        scale = removeScale(b);
        alpha = 0.45 * (1 - Math.max(0, gap) / (L * 1.5)) * Math.min(1, scale);
        var sw = extentX(b) * 2.2 * s * scale;
        var sh = L * 0.2 * s * scale;
        ctx.globalAlpha = alpha;
        ctx.drawImage(shadowSprite, vpX + b.x * s - sw / 2, floorScreenY(s) - sh * 0.55, sw, sh);
      }

      for (i = 0; i < list.length; i++) {
        b = list[i];
        if (b.fixed) {
          drawJake();
          continue;
        }
        scale = removeScale(b);
        if (scale <= 0.01) continue;
        s = scaleAt(b.p);
        alpha = b.removing ? Math.max(0, 1 - b.removeT * b.removeT) : 1;
        var dw = L * s * scale;
        var dh = (L / photoAspect(b.photo)) * s * scale;

        ctx.save();
        ctx.translate(vpX + b.x * s, floorScreenY(s) + b.y * s);
        ctx.rotate(b.a);

        // Soft contact shadow just below, onto whatever is underneath.
        ctx.globalAlpha = 0.3 * alpha;
        ctx.drawImage(shadowSprite, -dw * 0.52, -dh * 0.5 + capR * s * 0.3, dw * 1.04, dh * 1.1);

        ctx.globalAlpha = alpha;
        ctx.scale(b.flip, 1);
        var sprite = spriteFor(b.photo);
        if (sprite) {
          ctx.drawImage(sprite, -dw / 2, -dh / 2, dw, dh);
        } else {
          ctx.fillStyle = '#b8692b';
          ctx.beginPath();
          ctx.ellipse(0, 0, dw * 0.48, dh * 0.45, 0, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.restore();
      }
      ctx.globalAlpha = 1;
    }

    // Removal: a quick swell, then shrink to nothing.
    function removeScale(b) {
      if (!b.removing) return 1;
      var t = clamp(b.removeT, 0, 1);
      return t < 0.3 ? 1 + 0.15 * (t / 0.3) : 1.15 * (1 - (t - 0.3) / 0.7);
    }

    // ── Layout and resize ──

    // The longest a latke may be: a few must fit across the box at its
    // front, and one must fit front to back.
    function maxLength() {
      var front = Math.min(boxR - boxL, (W - 2 * EDGE - 0) / sFront);
      return Math.max(MIN_LENGTH, Math.min(front / 2.3, pFloor / (DEPTH * 1.6)));
    }

    // The room for latkes: the box up to the top of the card, in world px^3.
    function boxVolume() {
      var pMid = pFloor / 2;
      var sMid = scaleAt(pMid);
      var span = Math.max(1, wallRAt(pMid) - wallLAt(pMid));
      var height = Math.max(1, (floorScreenY(sMid) - fillTop) / sMid);
      return span * pFloor * height;
    }

    // The length at which n latkes fill the box.
    function fitLength(n) {
      return Math.pow((boxVolume() * FIT_FILL * PACKING) / (Math.max(1, n) * 2 * CAP_R * DEPTH), 1 / 3);
    }

    // How many latkes of the current size fit. At least max_latkes when
    // they are sized by fitLength(max); fewer only where MIN_LENGTH wins on
    // a very small screen.
    function capacity(L) {
      var each = L * L * L * 2 * CAP_R * DEPTH;
      return Math.max(1, Math.floor((boxVolume() * FIT_FILL * PACKING) / each + 0.001));
    }

    function setShape(L) {
      length = L;
      capR = CAP_R * L;
      capH = CAP_H * L;
      depthT = DEPTH * L;
      invI = 1 / (CAP_INERTIA * L * L);
      pMin = depthT * 0.5;
      pMax = Math.max(pMin, pFloor - depthT * 0.2);
    }

    // Zoom every latke by k about the middle of the floor, so nothing
    // overlaps that did not before.
    function rescale(k) {
      var pc = (pMin + pMax) / 2;
      var xc = (wallLAt(pc) + wallRAt(pc)) / 2;
      for (var i = 0; i < bodies.length; i++) {
        var b = bodies[i];
        b.x = xc + (b.x - xc) * k;
        b.y *= k;
        b.p = pc + (b.p - pc) * k;
        b.vx *= k;
        b.vy *= k;
        b.vp *= k;
        updateEnds(b);
        if (!b.removing) clampToBox(b);
      }
    }

    function measure() {
      var oldImgW = imgW;
      var oldLength = length;
      var hadLayout = W > 0;

      W = root.clientWidth;
      H = root.clientHeight;
      if (!W || !H) return false;
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.round(W * dpr);
      canvas.height = Math.round(H * dpr);

      // Where the photo lands on screen (CSS draws it as `cover`, anchored
      // bottom centre), and from that the box's perspective.
      var fit = Math.max(W / BG_W, H / BG_H);
      imgW = BG_W * fit;
      var imgH = BG_H * fit;
      var offX = (W - imgW) / 2;
      var offY = H - imgH;
      vpX = offX + VP_U * imgW;
      vpY = offY + VP_V * imgH;
      floorBackY = offY + BACK_FLOOR_V * imgH;
      camF = CAMERA_DISTANCE * imgW;
      boxL = offX + BACK_WALL_U_L * imgW - vpX;
      boxR = offX + BACK_WALL_U_R * imgW - vpX;
      sFront = Math.max(1.01, (H * (1 - FRONT_MARGIN) - vpY) / (floorBackY - vpY));
      pFloor = camF * (1 - 1 / sFront);
      sRef = (offY + REF_V * imgH - vpY) / (floorBackY - vpY);
      gravity = clamp(H * 2.6, 1400, 3200) / sRef;

      // A full box reaches the top of the card.
      fillTop = uiEl && cardEl ? uiEl.offsetTop + cardEl.offsetTop : H * 0.1;
      var refFloor = offY + REF_V * imgH;

      // The original round latkes' radius (see SIZE_FACTOR), on screen where
      // their floor was, then as a length at the back wall.
      var oldSpan = Math.max(1, Math.min(W - EDGE, offX + 0.9 * imgW) - Math.max(EDGE, offX + 0.1 * imgW));
      var oldR = Math.sqrt((oldSpan * H * REF_FREE * OLD_FILL) / (OLD_MAX * Math.PI));
      oldR = clamp(oldR, 9, Math.min(64, oldSpan / 5));
      baseLength = (SIZE_FACTOR * 2.3 * oldR) / sRef;
      smallLength = clamp(Math.min(baseLength, fitLength(max)), MIN_LENGTH, maxLength());
      bigLength = presetMax
        ? clamp(Math.min(baseLength, fitLength(presetMax)), smallLength, maxLength())
        : smallLength;
      targetLength = wantedLength();
      setShape(targetLength);
      cap = capacity(targetLength);
      spriteCache = [];
      jakeSprite = null;
      placeJake();

      if (hadLayout && bodies.length) {
        if (reduced) {
          layoutPile();
        } else {
          // World units follow the photo's size; then the latkes' own size.
          var k = imgW / oldImgW;
          var size = length / (oldLength * k);
          for (var i = 0; i < bodies.length; i++) {
            var b = bodies[i];
            b.x *= k;
            b.y *= k;
            b.p *= k;
            b.vx *= k;
            b.vy *= k;
            b.vp *= k;
          }
          rescale(size);
          wakeAll();
        }
      }
      return true;
    }

    // The big latkes while the set sizes are showing and the count is one
    // of them (or below the largest); otherwise the slider's.
    function wantedLength() {
      return !custom && count <= presetMax ? bigLength : smallLength;
    }

    // After a switch between the set sizes and the slider: ease the latkes
    // to their new size (frame() does the easing), or with reduced motion
    // lay them out again at it.
    function updateSize() {
      var want = wantedLength();
      if (want === targetLength) return;
      targetLength = want;
      cap = capacity(targetLength);
      if (reduced) {
        setShape(targetLength);
        syncBodies();
        layoutPile();
        draw();
        return;
      }
      wakeAll();
      start();
    }

    // One frame of easing towards targetLength: zoom the pile about the
    // middle of the floor, as on resize.
    function easeSize(dt) {
      if (length === targetLength) return;
      var next = length + (targetLength - length) * Math.min(1, dt * RESIZE_RATE);
      if (Math.abs(next - targetLength) < 0.5) next = targetLength;
      var k = next / length;
      setShape(next);
      rescale(k);
      wakeAll();
    }

    function onResize() {
      if (resizeRaf) return;
      resizeRaf = window.requestAnimationFrame(function () {
        resizeRaf = 0;
        if (!measure()) return;
        // The box may now hold fewer (or more) latkes.
        if (liveCount() !== shown()) syncBodies();
        if (reduced) draw();
        else start();
      });
    }

    // ── Stepper ──

    function hrefFor(n) {
      var joiner = sidesUrl.indexOf('?') === -1 ? '?' : '&';
      return sidesUrl + joiner + 'qty=' + encodeURIComponent(handle) + '@' + pack + ':' + n / pack;
    }

    // The field, the slider, the slider's filled track and the set size
    // buttons (the one matching, if any, is pressed) all show `count`.
    function syncUI() {
      input.value = String(count);
      slider.value = String(count);
      for (var i = 0; i < sizeButtons.length; i++) {
        var on = parseInt(sizeButtons[i].getAttribute('data-latke-size'), 10) === count;
        sizeButtons[i].setAttribute('aria-pressed', on ? 'true' : 'false');
      }
      var fill = max > pack ? ((count - pack) / (max - pack)) * 100 : 100;
      root.style.setProperty('--lb-fill', fill.toFixed(2) + '%');
      if (cta) cta.setAttribute('href', hrefFor(count));
    }

    function announce() {
      window.clearTimeout(announceTimer);
      announceTimer = window.setTimeout(function () {
        if (!statusEl) return;
        statusEl.textContent = count === 1 ? announceOne : announceMany.replace('[count]', String(count));
      }, 350);
    }

    function setCount(n) {
      n = toPacks(n);
      if (isNaN(n)) n = count;
      var changed = n !== count;
      count = n;
      syncUI();
      if (!changed) return;
      announce();
      updateSize();
      syncBodies();
    }

    function commitTyped() {
      var n = parseInt(String(input.value).replace(/[^0-9]/g, ''), 10);
      if (isNaN(n)) {
        syncUI();
        return;
      }
      setCount(n);
    }

    // Every step of a drag lands latkes (or takes them away) as it goes.
    function onSlide() {
      var n = parseInt(slider.value, 10);
      if (!isNaN(n)) setCount(n);
    }

    // "Custom size" swaps the set sizes for the slider and field; the link
    // under them swaps back. The count carries over either way.
    function showCustom(on) {
      if (!sizesEl || !pickerEl) return;
      custom = on;
      sizesEl.hidden = on;
      pickerEl.hidden = !on;
      updateSize();
      if (customBtn) customBtn.setAttribute('aria-expanded', on ? 'true' : 'false');
      if (on) slider.focus();
      else if (customBtn) customBtn.focus();
    }

    function onSizesClick(e) {
      var btn = e.target.closest ? e.target.closest('button') : null;
      if (!btn) return;
      if (btn === customBtn) {
        showCustom(true);
        return;
      }
      var n = parseInt(btn.getAttribute('data-latke-size'), 10);
      if (!isNaN(n)) setCount(n);
    }

    function onBack() {
      showCustom(false);
    }

    function onKeydown(e) {
      var key = e.key || '';
      if (key === 'Enter') {
        e.preventDefault();
        commitTyped();
      } else if (key === 'ArrowUp' || key === 'Up') {
        e.preventDefault();
        setCount(count + pack);
      } else if (key === 'ArrowDown' || key === 'Down') {
        e.preventDefault();
        setCount(count - pack);
      }
    }

    // A scroll wheel over a focused number field would change it silently.
    function onWheel(e) {
      if (document.activeElement === input) e.preventDefault();
    }

    function onMotionPreference() {
      reduced = !!(reduceMotionQuery && reduceMotionQuery.matches);
      if (reduced) {
        if (rafId) window.cancelAnimationFrame(rafId);
        rafId = 0;
        window.clearTimeout(pumpTimer);
        for (var i = bodies.length - 1; i >= 0; i--) {
          if (bodies[i].removing) bodies.splice(i, 1);
        }
        syncBodies();
      } else {
        wakeAll();
        start();
      }
    }

    // ── Wire up ──

    slider.max = String(max);
    slider.addEventListener('input', onSlide);
    input.addEventListener('change', commitTyped);
    input.addEventListener('keydown', onKeydown);
    input.addEventListener('wheel', onWheel, { passive: false });
    if (sizesEl) sizesEl.addEventListener('click', onSizesClick);
    if (backBtn) backBtn.addEventListener('click', onBack);

    if (window.ResizeObserver) {
      resizeObserver = new ResizeObserver(onResize);
      resizeObserver.observe(root);
    } else {
      window.addEventListener('resize', onResize);
    }

    if (reduceMotionQuery) {
      if (reduceMotionQuery.addEventListener) reduceMotionQuery.addEventListener('change', onMotionPreference);
      else if (reduceMotionQuery.addListener) reduceMotionQuery.addListener(onMotionPreference);
    }

    // The starting count rains into the empty box (or, with reduced motion,
    // is already lying there).
    count = startCount;
    measure();
    syncUI();
    draw();
    syncBodies();

    root.__latkeBox = {
      destroy: function () {
        if (rafId) window.cancelAnimationFrame(rafId);
        if (resizeRaf) window.cancelAnimationFrame(resizeRaf);
        window.clearTimeout(pumpTimer);
        window.clearTimeout(announceTimer);
        rafId = resizeRaf = pumpTimer = announceTimer = 0;
        slider.removeEventListener('input', onSlide);
        input.removeEventListener('change', commitTyped);
        input.removeEventListener('keydown', onKeydown);
        input.removeEventListener('wheel', onWheel);
        if (sizesEl) sizesEl.removeEventListener('click', onSizesClick);
        if (backBtn) backBtn.removeEventListener('click', onBack);
        if (resizeObserver) resizeObserver.disconnect();
        else window.removeEventListener('resize', onResize);
        if (reduceMotionQuery) {
          if (reduceMotionQuery.removeEventListener) reduceMotionQuery.removeEventListener('change', onMotionPreference);
          else if (reduceMotionQuery.removeListener) reduceMotionQuery.removeListener(onMotionPreference);
        }
        root.__latkeBox = null;
      }
    };
  }

  function initAll(scope) {
    var roots = (scope || document).querySelectorAll('[data-latke-box]');
    for (var i = 0; i < roots.length; i++) init(roots[i]);
  }

  // Theme editor: sections are re-rendered in place.
  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });
  document.addEventListener('shopify:section:unload', function (e) {
    var roots = e.target.querySelectorAll('[data-latke-box]');
    for (var i = 0; i < roots.length; i++) {
      if (roots[i].__latkeBox) roots[i].__latkeBox.destroy();
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }
})();
